<template>
    <div :class="(reportPayment? 'relative max-w-xl mx-auto' : '')">
        <svg v-if="reportPayment" class="absolute left-full transform translate-x-1/2" width="404" height="404" fill="none" viewBox="0 0 404 404" aria-hidden="true">
            <defs>
                <pattern id="85737c0e-0916-41d7-917f-596dc7edfa27" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                    <rect x="0" y="0" width="4" height="4" class="text-gray-200" fill="currentColor" />
                </pattern>
            </defs>
            <rect width="404" height="404" fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)" />
        </svg>
        <svg v-if="reportPayment" class="absolute right-full bottom-0 transform -translate-x-1/2" width="404" height="404" fill="none" viewBox="0 0 404 404" aria-hidden="true">
            <defs>
                <pattern id="85737c0e-0916-41d7-917f-596dc7edfa27" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                    <rect x="0" y="0" width="4" height="4" class="text-gray-200" fill="currentColor" />
                </pattern>
            </defs>
            <rect width="404" height="404" fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)" />
        </svg>
        <div v-if="reportPayment" class="text-center">
            <h2 class="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                Payment Information
            </h2>
        </div>
        <alert-card v-if="reportPayment && billing_type === 'report'" :type="'warning'">
            <p>
                Payment via the New Report button is not an application for a property.  Payment via this link will purchase a credit check and criminal background check only for your personal use and review.
            </p>
            <p class="mt-2">
                When applying for a property, a prospective tenant must apply via the RentRisk link associated with the property address. Contact the landlord or listing agent for the link.
            </p>
        </alert-card>

        <div :class="(reportPayment? 'mt-12' : 'space-y-6 bg-white rounded-md shadow-sm border border-gray-200 pb-6')">
            <div v-if="!reportPayment" class="sm:col-span-6 sm:flex justify-between items-center border-b border-gray-200 p-4">
                <div>
                    <h4 class="text-xl font-bold text-gray-700 sm:text-2xl sm:truncate">Payment</h4>
                    <!-- 86bc8uyd2: the warning above Purchase already says NO REFUNDS, so this
                         only shows where the signed acknowledgment replaces that warning. -->
                    <p v-if="acknowledgmentUrl" class="text-sm text-gray-500">Please note all payments are non-refundable.</p>
                </div>
            </div>
            <form :class="(reportPayment? 'grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8' : 'grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8 p-6 pt-0')" @submit.prevent="store">
                <div>
                    <label for="first_name" class="block text-sm font-medium text-gray-700">First name</label>
                    <div class="mt-1">
                        <input type="text" v-model="form.first_name" :error="form.errors.first_name" id="first_name" autocomplete="given_name" v-bind:class="(form.errors.first_name)?'block w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 border-red-500 rounded-md':'block w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 border-gray-300 rounded-md'">
                        <p v-text="form.errors.first_name" class="text-red-600"></p>
                    </div>
                </div>
                <div>
                    <label for="last_name" class="block text-sm font-medium text-gray-700">Last name</label>
                    <div class="mt-1">
                        <input type="text" v-model="form.last_name" :error="form.errors.last_name" id="last_name" autocomplete="family-name" v-bind:class="(form.errors.last_name)?'block w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 border-red-500 rounded-md':'block w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 border-gray-300 rounded-md'">
                        <p v-text="form.errors.last_name" class="text-red-600"></p>
                    </div>
                </div>
                <div>
                    <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
                    <div class="mt-1">
                        <input id="email" v-model="form.email" :error="form.errors.email" type="email" autocomplete="email" v-bind:class="(form.errors.email)?'block w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 border-red-500 rounded-md':'block w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 border-gray-300 rounded-md'">
                        <p v-text="form.errors.email" class="text-red-600"></p>
                    </div>
                </div>
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <div id="payment-element"></div>
                        <div id="error-message" class="bg-red-600 text-red-200 p-4 rounded-lg mt-8 hidden"></div>
                    </div>
                </div>
                <!-- RAC-10476: the application checkout asks for a signed acknowledgment
                     instead of the warning, saved before the card is charged. -->
                <div v-if="acknowledgmentUrl" class="sm:col-span-2 text-left" data-test="purchase-acknowledgment">
                    <label class="flex items-start gap-3 text-sm text-gray-700">
                        <input
                            v-model="acknowledged"
                            type="checkbox"
                            class="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400"
                            @change="onAcknowledgmentChanged"
                        >
                        <span>{{ acknowledgmentText }}</span>
                    </label>
                    <p v-if="acknowledgmentErrors.acknowledged" class="text-red-500 text-sm mt-2">{{ acknowledgmentErrors.acknowledged }}</p>
                    <div class="mt-4">
                        <signature-pad
                            ref="purchaseSignature"
                            label="Signature"
                            height="150px"
                            :required="true"
                            :error="acknowledgmentErrors.signature"
                            @end="onSignatureEnd"
                        />
                    </div>
                </div>
                <div v-else class="sm:col-span-2 text-left">
                    <!-- 86bcbu2dc: the self credit check is a search, not an application. -->
                    <alert-card v-if="billing_type === 'report'" :type="'warning'" data-test="report-warning">
                        Amount charged to your card: ${{ amount }}. Proceeding with this search creates an instant purchase of Experian reports and charges are incurred by RentRisk. Applicant understands that there are no refunds.
                    </alert-card>
                    <alert-card v-else :type="'warning'">
                        Amount charged to your card: ${{ amount }}. Applicant understands that there are NO REFUNDS. Proceeding with this application creates an instant purchase of credit and criminal reports from Experian and charges are incurred by RentRisk.
                    </alert-card>
                </div>
                <div class="sm:col-span-2">
                    <form-button id="purchase-btn" :form="form" :disabled="submitting" :data-secret="payment_intent.client_secret" class="-ml-1 w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-rent-risk-blue-400 hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400 disabled:bg-indigo-100" type="submit">
                        Purchase
                    </form-button>
                </div>
                <div class="sm:col-span-2 text-center">
                    <span class="text-xs tracking-tight text-gray-500">
                        By clicking Purchase, you agree to our <a :href="route('privacy-policy')" target="_blank" class="underline">Privacy Policy</a> and <a :href="route('terms-and-conditions')" target="_blank" class="underline">Terms and Conditions</a>.
                    </span>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import FormButton from '@/Shared/FormButton.vue'
import { computed, markRaw } from "vue";
import { usePage, useForm } from "@inertiajs/vue3";
import AlertCard from "@/Shared/AlertCard.vue";
import SignaturePad from '@/Components/SignaturePad.vue';
import axios from 'axios';

export default {
    props: {
        publishable_key: String,
        amount: {
            type: String,
            default: '59'
        },
        first_name: {
            type: String,
            default: ''
        },
        last_name: {
            type: String,
            default: ''
        },
        billing_type: {
            type: String,
            default: 'application'
        },
        report_id: {
            type: String,
            default: ''
        },
        application_id: {
            type: String,
            default: ''
        },
        user: Object,
        payment_intent: Object,
        reportPayment: {
            type: Boolean,
            default: false
        },
        returnUrl: {
            type: String,
            default: null
        },
        // RAC-10476: when set, Purchase first requires the signed acknowledgment and
        // posts it here. Left unset, the checkout keeps the plain warning.
        acknowledgmentUrl: {
            type: String,
            default: null
        },
        // The checkbox copy, sent by the server from LegalDocuments so the checkout
        // always shows the text quoted as dispute evidence.
        acknowledgmentText: {
            type: String,
            default: ''
        }
    },
    mounted(){
        this.includeStripe('js.stripe.com/v3/', function(){
            this.configureStripe();
        }.bind(this) );
    },
    beforeUnmount(){
        // 86bcfenhv: drop a pending background pre-save so it never fires after teardown.
        if (this.acknowledgmentPreSaveTimer) {
            clearTimeout(this.acknowledgmentPreSaveTimer);
            this.acknowledgmentPreSaveTimer = null;
        }
    },
    setup() {
        // Falls back to the signed-in user when no user prop is passed.
        const pageUser = computed(() => usePage().props.auth?.user);
        return {pageUser};
    },
    components: {
        FormButton,
        AlertCard,
        SignaturePad
    },
    data() {
        return {
            acknowledged: false,
            acknowledgmentErrors: { acknowledged: null, signature: null },
            // 86bcfenhv: Apple Pay requires confirmPayment to run straight from the
            // Purchase click, so the signed acknowledgment is saved as the applicant
            // completes it rather than between the click and the charge. These track
            // whether it is already on file and which signature was saved, so a redraw
            // saves again but an unchanged one never saves twice.
            acknowledgmentSaved: false,
            acknowledgmentSaving: false,
            acknowledgmentSavedSignature: null,
            acknowledgmentPreSaveTimer: null,
            acknowledgmentSavePromise: null,
            submitting: false,
            button_active_state: false,
            stripeAPIToken: this.publishable_key,
            stripe: null,
            stripeReady: false,
            elements: '',
            paymentElement: null,
            card: '',
            form: useForm({
                first_name: this.first_name,
                last_name: this.last_name,
                billing_type: this.billing_type,
                report_id: this.report_id,
                application_id: this.application_id,
                email: (this.user || this.pageUser)?.email || '',
                card: null,
                payment_intent: null,
                token: null,
            }),
            style: {
                base: {
                    iconColor: "#c4f0ff",
                    color: "#333",
                    fontWeight: "500",
                    fontFamily: "Roboto, Open Sans, Segoe UI, sans-serif",
                    fontSize: "16px",
                    fontSmoothing: "antialiased",
                    ":-webkit-autofill": {
                        color: "#fce883",
                    },
                    "::placeholder": {
                        color: "#333",
                    },
                },
                invalid: {
                    iconColor: "#FFC7EE",
                    color: "#FFC7EE",
                },
            },
        }
    },
    watch: {
        payment_intent(newVal, oldVal) {
            if (newVal && this.stripe && (!oldVal || newVal.client_secret !== oldVal.client_secret)) {
                if (this.paymentElement) {
                    this.paymentElement.unmount();
                }
                this.configureStripe();
            }
        },
    },
    methods: {
        includeStripe( URL, callback ){
            let documentTag = document, tag = 'script',
                object = documentTag.createElement(tag),
                scriptTag = documentTag.getElementsByTagName(tag)[0];
            object.src = '//' + URL;
            if (callback) { object.addEventListener('load', function (e) { callback(null, e); }, false); }
            scriptTag.parentNode.insertBefore(object, scriptTag);
        },

        /*
            Configures Stripe by setting up the elements and
            creating the card element.
        */
        configureStripe(){
            this.stripe = Stripe( this.stripeAPIToken );
            const options = {
                clientSecret: this.payment_intent.client_secret,
                // Fully customizable with appearance API.
                appearance: {/*...*/},
            };
            this.elements = this.stripe.elements(options);
            // markRaw: keep Vue's reactivity proxy off the Stripe element.
            // 86bcfenhv: no paymentMethodTypes here — the payment methods come from the
            // PaymentIntent (card). Apple Pay and Google Pay stay on `auto` so they show
            // once the domain is registered with Stripe; Link is turned off so the only
            // wallets offered are the two we intend.
            this.paymentElement = markRaw(this.elements.create('payment', {
                wallets: { link: 'never' },
            }));

            this.paymentElement.mount('#payment-element');
            this.stripeReady = true;
        },
        showPaymentError(message) {
            const messageContainer = document.querySelector('#error-message');
            messageContainer.classList.remove("hidden");
            messageContainer.textContent = message;
        },
        /*
            RAC-10476: the box is ticked and the pad is signed. Checked before
            anything else so the applicant sees these errors straight away.
        */
        acknowledgmentIsComplete() {
            const signaturePad = this.$refs.purchaseSignature;
            this.acknowledgmentErrors = {
                acknowledged: this.acknowledged ? null : 'Please tick the box to acknowledge this purchase.',
                signature: signaturePad && !signaturePad.isEmpty() ? null : 'Please sign your name in the Signature box before purchasing.',
            };

            if (this.acknowledgmentErrors.acknowledged || this.acknowledgmentErrors.signature) {
                signaturePad?.$el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
                return false;
            }

            return true;
        },
        /*
            RAC-10476: save the signed acknowledgment. The charge only goes ahead
            once it is on file, so a failed save means no charge. 86bcfenhv: on
            success it records which signature is on file, so the Purchase click can
            skip straight to the charge and a redraw is saved again while an unchanged
            signature is never saved twice.
        */
        async saveAcknowledgment({ surfaceErrors = true } = {}) {
            const signaturePad = this.$refs.purchaseSignature;
            const signature = signaturePad.save();
            try {
                const response = await axios.post(this.acknowledgmentUrl, {
                    acknowledged: true,
                    signature,
                });
                // Middleware can answer with a redirect (e.g. expired Experian
                // verification) that axios follows to a 200 page — only the
                // endpoint's own confirmation counts as saved.
                if (response?.data?.status !== true) {
                    throw new Error('Acknowledgment was not confirmed');
                }
                this.acknowledgmentSaved = true;
                this.acknowledgmentSavedSignature = signature;
                return true;
            } catch (error) {
                // 86bcfenhv: a failed background pre-save stays silent — the applicant
                // has not clicked Purchase yet, so the click-time save surfaces errors.
                if (surfaceErrors) {
                    const errors = error?.response?.data?.errors ?? {};
                    this.acknowledgmentErrors = {
                        acknowledged: errors.acknowledged?.[0] ?? null,
                        signature: errors.signature?.[0]
                            ?? (errors.acknowledged ? null : 'We could not save your signature. Please try again.'),
                    };
                }
                return false;
            }
        },
        /*
            86bcfenhv: the box was ticked or unticked. Clear its error, and after an
            untick require a fresh save so a re-tick records the acknowledgment again.
        */
        onAcknowledgmentChanged() {
            this.acknowledgmentErrors.acknowledged = null;
            if (!this.acknowledged) {
                // Unticking needs a fresh save before a re-tick can back a charge; there
                // is nothing to pre-save until it is ticked again.
                this.acknowledgmentSaved = false;
                return;
            }
            this.schedulePreSaveAcknowledgment();
        },
        /*
            86bcfenhv: the applicant finished a signature stroke. A signature that
            differs from the one on file must be saved again before it can back a charge.
        */
        onSignatureEnd() {
            this.acknowledgmentErrors.signature = null;
            const signaturePad = this.$refs.purchaseSignature;
            if (
                this.acknowledgmentSaved
                && signaturePad
                && signaturePad.save() !== this.acknowledgmentSavedSignature
            ) {
                this.acknowledgmentSaved = false;
            }
            this.schedulePreSaveAcknowledgment();
        },
        /*
            86bcfenhv: debounce the background save so a multi-stroke signature is saved
            once it settles, not after every stroke.
        */
        schedulePreSaveAcknowledgment() {
            if (this.acknowledgmentPreSaveTimer) {
                clearTimeout(this.acknowledgmentPreSaveTimer);
            }
            this.acknowledgmentPreSaveTimer = setTimeout(() => {
                this.acknowledgmentPreSaveTimer = null;
                this.preSaveAcknowledgment();
            }, 500);
        },
        /*
            86bcfenhv: save the acknowledgment ahead of the Purchase click, so Apple Pay's
            sheet can open directly from the click with no network call in between. Runs
            only once per completed, unchanged box-and-signature pair.
        */
        async preSaveAcknowledgment() {
            if (!this.acknowledgmentUrl || !this.acknowledged) {
                return;
            }
            const signaturePad = this.$refs.purchaseSignature;
            if (!signaturePad || signaturePad.isEmpty()) {
                return;
            }
            if (this.acknowledgmentSaved && signaturePad.save() === this.acknowledgmentSavedSignature) {
                return;
            }
            if (this.acknowledgmentSaving) {
                // A save is already in flight. Re-arm so a signature redrawn meanwhile is
                // still saved once that one settles, instead of being silently dropped.
                this.schedulePreSaveAcknowledgment();
                return;
            }
            this.acknowledgmentSaving = true;
            this.acknowledgmentSavePromise = this.saveAcknowledgment({ surfaceErrors: false });
            try {
                await this.acknowledgmentSavePromise;
            } finally {
                this.acknowledgmentSaving = false;
                this.acknowledgmentSavePromise = null;
            }
        },
        async store() {
            if (!this.stripeReady || typeof this.stripe?.confirmPayment !== 'function') {
                const messageContainer = document.querySelector('#error-message');
                if (messageContainer) {
                    messageContainer.classList.remove('hidden');
                    messageContainer.textContent = 'Payment system is still loading. Please wait a moment and try again.';
                }
                return;
            }

            // RAC-10476: one submit at a time — the acknowledgment and the charge are
            // two round trips, and a second click between them would repeat both.
            if (this.submitting) {
                return;
            }
            this.submitting = true;

            let error;
            try {
                var elements = this.elements;

                if (this.acknowledgmentUrl) {
                    if (!this.acknowledgmentIsComplete()) {
                        return;
                    }

                    // 86bcfenhv: let any background save settle before deciding, so the
                    // click never starts a second save beside one already in flight.
                    if (this.acknowledgmentPreSaveTimer) {
                        clearTimeout(this.acknowledgmentPreSaveTimer);
                        this.acknowledgmentPreSaveTimer = null;
                    }
                    if (this.acknowledgmentSavePromise) {
                        await this.acknowledgmentSavePromise;
                    }

                    const signaturePad = this.$refs.purchaseSignature;
                    const alreadyOnFile = this.acknowledgmentSaved
                        && signaturePad
                        && signaturePad.save() === this.acknowledgmentSavedSignature;

                    // When the acknowledgment is already on file — the common path, since
                    // the box and signature are completed before Purchase — fall through to
                    // confirmPayment so Apple Pay's sheet opens straight from the click.
                    // Otherwise save it inline, checking the card first so an incomplete
                    // card doesn't record a signed acknowledgment on every attempt.
                    if (!alreadyOnFile) {
                        const { error: cardError } = await elements.submit();
                        if (cardError) {
                            this.showPaymentError(cardError.message);
                            return;
                        }

                        if (!(await this.saveAcknowledgment())) {
                            return;
                        }
                    }
                }

                const return_url = this.returnUrl || route('post.billing',{'report_id':this.report_id,'billing_type':this.billing_type,'application_id':this.application_id});

                ({error} = await this.stripe.confirmPayment({
                    //`Elements` instance that was used to create the Payment Element
                    elements,
                    confirmParams: {
                        return_url: return_url,
                    },
                }));
            } finally {
                this.submitting = false;
            }

            if (error) {
                // This point will only be reached if there is an immediate error when
                // confirming the payment. Show error to your customer (for example, payment
                // details incomplete)
                console.log(JSON.stringify(error));
                this.showPaymentError(error.message);
            } else {
                // const messageContainer = document.querySelector('#error-message');
                // messageContainer.classList.add("hidden");
                // Your customer will be redirected to your `return_url`. For some payment
                // methods like iDEAL, your customer will be redirected to an intermediate
                // site first to authorize the payment, then redirected to the `return_url`.
            }
        },
    }
}
</script>